import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import api from "../api";

const AuthContext = createContext(null);

// Users can hold several roles (e.g. member + secretary). The portal works in one
// role at a time; the chosen role is remembered per user in this browser.
const ACTIVE_ROLE_KEY = "tarewu_active_role";

const readActiveRole = (userId) => {
  try {
    const stored = JSON.parse(localStorage.getItem(ACTIVE_ROLE_KEY) || "null");
    return stored && stored.userId === userId ? stored.role : null;
  } catch (e) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null);

  useEffect(() => {
    setActiveRole(user ? readActiveRole(user.id) : null);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleOptions = useMemo(() => (Array.isArray(user?.role_options) ? user.role_options : []), [user]);

  // The user as the rest of the portal sees it: role, roles and permissions of the active role only.
  const effectiveUser = useMemo(() => {
    if (!user || roleOptions.length < 2) return user;
    const option = roleOptions.find((o) => o.slug === activeRole) || roleOptions.find((o) => o.slug === user.role) || roleOptions[0];
    return { ...user, role: option.slug, role_name: option.name, roles: [option.slug], permissions: option.permissions || [], primary_role: user.role, all_roles: user.roles };
  }, [user, roleOptions, activeRole]);

  const switchRole = (slug) => {
    if (!user || !roleOptions.some((o) => o.slug === slug)) return;
    localStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify({ userId: user.id, role: slug }));
    setActiveRole(slug);
  };

  useEffect(() => {
    // Check for stored token and fetch current user
    const token = localStorage.getItem("tarewu_token");
    if (token) {
      api
        .me()
        .then((res) => {
          if (res && res.data) {
            const user = res.data;
            localStorage.setItem("tarewu_user", JSON.stringify(user));
            setUser(user);
          } else {
            localStorage.removeItem("tarewu_user");
            localStorage.removeItem("tarewu_token");
            setUser(null);
          }
        })
        .catch(() => {
          localStorage.removeItem("tarewu_user");
          localStorage.removeItem("tarewu_token");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleUnauthorized = (event) => {
      setUser(null);
      localStorage.removeItem("tarewu_user");
      localStorage.removeItem("tarewu_token");
      sessionStorage.setItem(
        "tarewu_auth_message",
        event.detail?.message || "Please sign in again.",
      );
    };
    window.addEventListener("tarewu:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("tarewu:unauthorized", handleUnauthorized);
  }, []);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    if (!res || !res.data) throw new Error("Login failed");
    if (res.data.otp_required) return res.data;
    return persistLogin(res.data);
  };
  const persistLogin = (data) => {
    if (!data?.token || !data?.user) throw new Error("Login failed");
    const token = data.token;
    const user = data.user;
    // Normalize role to slug (in case backend returns role_id only)
    if (data.user && data.user.role) {
      user.role = data.user.role;
    }
    localStorage.setItem("tarewu_token", token);
    localStorage.setItem("tarewu_user", JSON.stringify(user));
    setUser(user);
    return user;
  };
  const verifyElectionLoginOTP = async (challenge, otp) => {
    const res = await api.verifyElectionLoginOTP(challenge, otp);
    return persistLogin(res.data);
  };
  const logout = async () => {
    // Clear the client session immediately so authenticated background effects
    // stop before the server revokes the token.
    const request = api.logout();
    setUser(null);
    localStorage.removeItem("tarewu_user");
    localStorage.removeItem("tarewu_token");
    try {
      await request;
    } catch (e) {
      // ignore
    }
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
    localStorage.setItem(
      "tarewu_user",
      JSON.stringify({ ...user, ...userData }),
    );
  };

  const hasRole = (roles) => {
    if (!effectiveUser) return false;
    const userRoles = Array.isArray(effectiveUser.roles) && effectiveUser.roles.length ? effectiveUser.roles : [effectiveUser.role];
    if (typeof roles === "string") {
      return userRoles.includes(roles);
    }
    return roles.some((role) => userRoles.includes(role));
  };

  const hasPermission = (permission) => {
    if (!effectiveUser || !permission) return false;
    const permissions = Array.isArray(effectiveUser.permissions) ? effectiveUser.permissions : [];
    if (permissions.includes('*') || permissions.includes(permission))
      return true;
    const domain = permission.split(".")[0];
    return permissions.includes(`${domain}.*`);
  };

  const value = {
    user: effectiveUser,
    roleOptions,
    switchRole,
    loading,
    login,
    verifyElectionLoginOTP,
    logout,
    updateUser,
    hasRole,
    hasPermission,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
